import { MONEY_SCALE, ZERO_MONEY, fromString, round, toString, } from '@aeris/money';
/**
 * The till session.
 *
 * Master Specification section 18 lists what a PDV must support: abertura de
 * caixa, sangria, suprimento, venda, cancelamento, recebimento, fechamento,
 * diferença. This package holds the part underneath all of them — the session
 * an operator opens at the start of a shift and closes at the end, and the
 * ledger of everything that moved cash while it was open.
 *
 * Two invariants shape everything here:
 *
 *   1. **The ledger is the truth.** `cash_movement` is append-only, by
 *      privilege rather than by convention (migration 0011). The same argument
 *      as inventory: a drawer whose history can be rewritten accounts for
 *      nothing.
 *
 *   2. **The expected amount is derived, never typed.** The operator types what
 *      they *counted*; `expectedAmount()` sums the ledger; the difference is
 *      computed. There is no input anywhere in this package, the contracts or
 *      the API that could carry an expected figure — a till where somebody can
 *      type it is a till that never has a difference.
 *
 * Amounts are `Decimal` throughout, never a JavaScript number. ADR-0016.
 */
// ---------------------------------------------------------------------------
// Movement kinds
// ---------------------------------------------------------------------------
/**
 * The six things that move cash in a shift.
 *
 * `sale` and `refund` are here although nothing in F014 emits them, and that is
 * deliberate. The expected balance is a sum over this vocabulary, and the sum
 * has to be defined over the complete set from the first day — otherwise a
 * session opened before the sales module ships and closed after it would be
 * reconciled by two different definitions of "expected". The same reasoning put
 * `reserved` on `stock_balance` before anything could reserve.
 */
export const CASH_MOVEMENT_KINDS = [
    /**
     * The fundo de troco the drawer started with. At most one per session — a
     * till opened with an empty drawer writes none, because zero is not a
     * movement.
     */
    'opening',
    /** Cash taken for a sale. Written by the sales module, not by this one. */
    'sale',
    /** Cash returned: a cancellation, a devolução. Also not written here yet. */
    'refund',
    /** Sangria — cash removed to a safe or a bank. */
    'withdrawal',
    /** Suprimento — cash added mid-shift, usually to make change. */
    'deposit',
    /** A correction, in either direction, with a reason and a name attached. */
    'adjustment',
    /**
     * Um gasto da loja pago com dinheiro da gaveta: a marmita, a bobina, o
     * salário em espécie (F146). Não é sangria — sangria leva o dinheiro ao
     * cofre, e o relatório do turno que a confundisse com a marmita mentiria.
     * Escrito pelo caderno de gastos, não por esta tela.
     */
    'expense',
];
export const isCashMovementKind = (value) => typeof value === 'string' &&
    CASH_MOVEMENT_KINDS.includes(value);
/**
 * Which way a movement pushes the drawer.
 *
 * Stored alongside the kind rather than only derived from it, because `amount`
 * is always positive and something has to carry the sign. A signed amount was
 * the alternative and is worse: `-50` and `50` in one column means every
 * aggregate and every report has to remember which it is looking at, and
 * `sum(amount)` silently becomes a net figure that reads like a turnover.
 */
export const CASH_MOVEMENT_DIRECTIONS = ['in', 'out'];
/**
 * The direction a kind fixes, or `null` when the kind does not fix one.
 *
 * Five of the six are unambiguous. `adjustment` is not, and cannot be: a
 * correction can go either way and both outcomes are the same operation. So it
 * is the one kind whose direction the caller states, and a check constraint in
 * migration 0011 pins the other five so a row can never claim a sangria that
 * adds cash.
 *
 * Returning `null` rather than defaulting to `'in'` is the point: a default
 * here would let a shortfall be silently recorded as a deposit.
 */
export const directionOf = (kind) => {
    switch (kind) {
        case 'opening':
        case 'sale':
        case 'deposit':
            return 'in';
        case 'refund':
        case 'withdrawal':
        case 'expense':
            return 'out';
        case 'adjustment':
            return null;
    }
};
/**
 * What caused a movement — the loose reference described in migration 0011.
 *
 * `sales_document` and `payment` name tables that do not exist in this
 * migration and are being built by other features. There is deliberately no
 * foreign key; see the long note in `0011_cash.sql` for why that is a choice
 * rather than an omission, and for what it gives up.
 */
export const CASH_SOURCE_TYPES = [
    /** The session's own opening float. */
    'opening',
    /** A person at the till: sangria, suprimento, adjustment. */
    'manual',
    /** A sale document (section 17). Nothing in F014 writes this. */
    'sales_document',
    /** A payment (section 21). Nothing in F014 writes this either. */
    'payment',
    /**
     * A baixa de um título (F034, ADR-0021 decisão 9).
     *
     * O dinheiro que o motoboy traz da rua entra na gaveta quando alguém baixa o
     * título — e não é uma venda de balcão nem um pagamento do PDV. Chamá-la de
     * um dos dois faria o relatório do turno mentir sobre o que aconteceu no
     * salão.
     */
    'finance_settlement',
    /** Um lançamento do caderno de gastos ou da folha (F146). */
    'expense',
];
/**
 * Whether a source type carries an id.
 *
 * A `sales_document` reference with no id points at nothing; a `manual` one
 * carrying an id invites a reader to look for a record that never existed.
 * Migration 0011 constrains both directions.
 */
export const sourceRequiresId = (source) => source === 'sales_document' ||
    source === 'payment' ||
    source === 'finance_settlement' ||
    source === 'expense';
// ---------------------------------------------------------------------------
// Session status
// ---------------------------------------------------------------------------
export const CASH_SESSION_STATUSES = ['open', 'closed'];
// ---------------------------------------------------------------------------
// Terminals
// ---------------------------------------------------------------------------
/** Longest terminal name the column accepts. See migration 0011. */
export const MAX_TERMINAL_ID_LENGTH = 40;
/**
 * Normalises a terminal identifier.
 *
 * Uppercased, trimmed, and internal runs of whitespace collapsed. Without this,
 * "caixa 01" and "CAIXA  01" are two different tills as far as the partial
 * unique index is concerned — and the invariant that matters most in this
 * feature would be defeated by a space bar.
 */
export const normalizeTerminalId = (value) => value.trim().replace(/\s+/g, ' ').toUpperCase();
export const isValidTerminalId = (value) => {
    const normalized = normalizeTerminalId(value);
    return normalized.length > 0 && normalized.length <= MAX_TERMINAL_ID_LENGTH;
};
export const CASH_TERMINAL_STATUSES = ['active', 'archived'];
// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------
/** R$ 0,00 at the scale the database column carries. */
export const ZERO_CASH = ZERO_MONEY;
/**
 * Reads an amount from a string.
 *
 * A string, always: NUMERIC arrives from the driver as text (see
 * `packages/database/src/client.ts`) and travels the wire as text, precisely so
 * that no boundary can round it. Returns null rather than throwing, because an
 * unparseable amount is ordinary user input rather than a programming error.
 */
export const parseCashAmount = (value) => {
    try {
        return round(fromString(value.trim()), MONEY_SCALE, 'half-up');
    }
    catch {
        return null;
    }
};
/** The wire and database format. Never a JSON number. */
export const cashAmountToString = (value) => toString(value);
//# sourceMappingURL=entities.js.map